import {
  MonitoringTenantUser,
  MonitoringUsersGroup,
} from '@hakimo-ui/hakimo/types';
import {
  HakimoSpinner,
  InputField,
  SegmentedToggle,
} from '@hakimo-ui/shared/ui-base';
import { useEffect, useMemo, useState } from 'react';
import {
  MONITORING_USERS_ACCESS_FILTER_OPTIONS,
  MonitoringUsersAccessFilter,
} from '../util';
import { MonitoringUserGroup, getEffectiveAccess } from './MonitoringUserGroup';

interface Props {
  groups: MonitoringUsersGroup[] | undefined;
  isLoading: boolean;
  isUpdating: boolean;
  pending: Record<string, boolean>;
  onToggleUser: (userId: string, hasAccess: boolean, checked: boolean) => void;
}

function matchesSearch(user: MonitoringTenantUser, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    user.name.toLowerCase().includes(q) || user.email.toLowerCase().includes(q)
  );
}

export function MonitoringUsersList({
  groups,
  isLoading,
  isUpdating,
  pending,
  onToggleUser,
}: Props) {
  const [search, setSearch] = useState('');
  const [accessFilter, setAccessFilter] =
    useState<MonitoringUsersAccessFilter>('all');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!groups) return;
    setCollapsed((prev) => {
      const next = { ...prev };
      groups.forEach((g) => {
        if (!(g.tenantId in next)) next[g.tenantId] = true;
      });
      return next;
    });
  }, [groups]);

  const filteredGroups = useMemo(() => {
    if (!groups) return [];
    const isFiltering = !!search || accessFilter !== 'all';
    return groups
      .map((g) => ({
        ...g,
        users: g.users.filter((u) => {
          if (!matchesSearch(u, search)) return false;
          if (accessFilter === 'all') return true;
          const hasAccess = getEffectiveAccess(u, pending);
          return accessFilter === 'enabled' ? hasAccess : !hasAccess;
        }),
      }))
      .filter((g) => g.users.length > 0 || !isFiltering);
  }, [groups, search, accessFilter, pending]);

  const toggleCollapsed = (tenantId: string) =>
    setCollapsed((prev) => ({ ...prev, [tenantId]: !prev[tenantId] }));

  const showInitialLoader = isLoading && !groups;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col p-6">
      <div className="mb-3 flex items-center gap-2">
        <InputField
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search users by name or email"
        />
        <SegmentedToggle
          size="small"
          value={accessFilter}
          options={MONITORING_USERS_ACCESS_FILTER_OPTIONS}
          onChange={(value) =>
            setAccessFilter(value as MonitoringUsersAccessFilter)
          }
          disabled={isUpdating}
        />
      </div>
      <div className="relative min-h-0 flex-1 space-y-4 overflow-y-auto pr-4">
        {!showInitialLoader && filteredGroups.length === 0 && (
          <p className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
            No users found.
          </p>
        )}
        {!showInitialLoader &&
          filteredGroups.map((g) => (
            <MonitoringUserGroup
              key={g.tenantId}
              tenantId={g.tenantId}
              tenantName={g.tenantName}
              users={g.users}
              withAccessCount={g.withAccessCount}
              totalUsers={g.totalUsers}
              collapsed={collapsed[g.tenantId]}
              isUpdating={isUpdating}
              pending={pending}
              onToggleCollapsed={toggleCollapsed}
              onToggleUser={onToggleUser}
            />
          ))}
      </div>
      {isLoading && (
        <div className="bg-onlight-bg-1/60 dark:bg-ondark-bg-1/60 pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <HakimoSpinner />
        </div>
      )}
    </div>
  );
}

export default MonitoringUsersList;
